import React, {useCallback, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {YesNoModal} from '../../elements/YesNoModal';
import {activateLine, deactivateLine, deleteLine} from './lineManageActions';

/**
 * El estado de confirmación de las acciones del menú `⋮` de una línea, con su modal.
 *
 * Vive acá y no en cada shell porque lo necesitan LOS DOS —el Panel (`PanelV11Content`) y la vista
 * de todas las líneas (`LinesView`)— y son exactamente el tipo de par que diverge: la rama de
 * `needsLinkConfirm` es fácil de olvidar en la segunda copia, y el usuario se entera recién cuando
 * reactiva una línea desde la pantalla equivocada.
 *
 * **Qué confirma y qué no:**
 * - **Eliminar** → confirma. Es la única irreversible.
 * - **Desactivar** → confirma. No porque sea irreversible (no lo es, y el hint del drawer lo dice),
 *   sino porque desde el menú es UN click sobre una lista de doce filas, sin más contexto que el
 *   nombre. La sección «Gestión» del drawer sigue desactivando directo y eso no es incoherencia:
 *   ahí el nombre de la línea está en el encabezado y su consumo a la vista, o sea que la
 *   intención ya está evidenciada.
 * - **Reactivar** → NO confirma: es re-encender el monitoreo. Puede terminar igual en el modal si
 *   el backend pide permiso para vincular las cuentas al cliente (`needsLinkConfirm`), que no es
 *   una confirmación de riesgo sino una pregunta suya.
 *
 * `onDone` es el refetch de la superficie: se llama después de CUALQUIER escritura exitosa.
 */
export function useLineManageConfirm(onDone) {
    const {t} = useTranslation();
    // `{type: 'deactivate' | 'delete' | 'activate_link', line}`
    const [confirmAction, setConfirmAction] = useState(null);
    /**
     * Una escritura en vuelo a la vez, y el DELETE no se puede deshacer.
     *
     * Son DOS cosas y hacen falta las dos: el estado alimenta el `isSaving` del botón «Sí» (que es
     * un `SaveButton` con `hasChanges` fijo en `true`, o sea que sin esto no se deshabilita nunca
     * ni muestra spinner), y el **ref** es el que de verdad frena un segundo click.
     *
     * ⚠️ El guard NO puede leer el estado: dos clicks en el mismo tick ven el mismo valor del
     * closure —todavía `false`— y los dos pasan. Un ref se actualiza sincrónicamente. Verificado
     * por mutación: con el guard sobre el estado, romperlo no hacía caer ningún test.
     */
    const [escribiendo, setEscribiendo] = useState(false);
    const enVuelo = useRef(false);

    const marcarEscritura = useCallback((activa) => {
        enVuelo.current = activa;
        setEscribiendo(activa);
    }, []);

    const settle = useCallback((result) => {
        marcarEscritura(false);
        if (result.needsLinkConfirm) return false;
        if (result.ok) {
            toast.success(result.message);
            if (onDone) onDone();
        } else {
            // El `error_message` del backend sale a pantalla a propósito: ver `lineManageActions`.
            toast.error(result.message);
        }
        return true;
    }, [marcarEscritura, onDone]);

    const requestDeactivate = useCallback((line) => setConfirmAction({type: 'deactivate', line}), []);
    const requestDelete = useCallback((line) => setConfirmAction({type: 'delete', line}), []);

    const requestActivate = useCallback((line) => {
        if (enVuelo.current) return;
        marcarEscritura(true);
        activateLine(line.id, {addAccountsToClient: false}, t).then((result) => {
            if (!settle(result)) setConfirmAction({type: 'activate_link', line});
        });
    }, [marcarEscritura, settle, t]);

    const cancel = useCallback(() => setConfirmAction(null), []);

    const confirmYes = useCallback(() => {
        if (enVuelo.current) return;
        const action = confirmAction;
        setConfirmAction(null);
        if (!action) return;
        marcarEscritura(true);
        const lineId = action.line.id;
        if (action.type === 'deactivate') {
            deactivateLine(lineId, t).then(settle);
        } else if (action.type === 'delete') {
            deleteLine(lineId, t).then(settle);
        } else if (action.type === 'activate_link') {
            activateLine(lineId, {addAccountsToClient: true}, t).then(settle);
        }
    }, [confirmAction, marcarEscritura, settle, t]);

    return {confirmAction, escribiendo, requestDeactivate, requestActivate, requestDelete,
        confirmYes, cancel};
}

/**
 * El modal de las tres confirmaciones. `YesNoModal` con `skipRenderButton` y el `isOpen` mandado
 * desde afuera — el mismo patrón que el Listado v2 (`AgencyClientsV2Content.renderConfirmModal`).
 *
 * ⚠️ Los botones «Sí»/«No» de ese componente están hardcodeados en español; es deuda preexistente
 * de un componente que usa media app, y no se arregla desde acá.
 */
export function LineManageConfirmModal({action, onYes, onNo, isSaving}) {
    const {t} = useTranslation();
    if (!action) return null;

    const name = action.line.name;
    const copyByType = {
        deactivate: {
            title: t('client_panel.line_deactivate_confirm_title', 'Desactivar la línea de seguimiento'),
            message: t('client_panel.line_deactivate_confirm_message',
                'Deja de sincronizar, no dispara alertas y sale del Panel. Se puede reactivar cuando quieras.'),
            question: t('client_panel.line_deactivate_confirm_question', '¿Desactivar «{{name}}»?',
                {name, interpolation: {escapeValue: false}}),
        },
        delete: {
            title: t('client_panel.line_delete_confirm_title', 'Eliminar la línea de seguimiento'),
            message: t('client_panel.line_delete_confirm_message',
                'Se elimina la línea y toda su configuración. Esto no se puede deshacer.'),
            question: t('client_panel.line_delete_confirm_question', '¿Eliminar «{{name}}»?',
                {name, interpolation: {escapeValue: false}}),
        },
        // Lo pregunta el BACKEND al reactivar, y el copy es el mismo que ya usaban Monitoreo y la
        // sección «Gestión» del drawer: es la misma pregunta, no puede sonar distinta según desde
        // dónde se reactive.
        activate_link: {
            title: t('clients.link_ad_accounts_title'),
            message: t('client_panel.line_manage_link_accounts_question',
                'Las cuentas de esta línea no están vinculadas al cliente. ¿Vincularlas y reactivar la línea?'),
            question: t('client_panel.line_activate_link_question', '¿Vincular y reactivar «{{name}}»?',
                {name, interpolation: {escapeValue: false}}),
        },
    };
    const copy = copyByType[action.type];
    if (!copy) return null;

    return (
        <YesNoModal
            isOpen={true}
            skipRenderButton={true}
            title={copy.title}
            message={copy.message}
            question={copy.question}
            onYesAnswer={onYes}
            onNoAnswer={onNo}
            isSaving={isSaving}
        />
    );
}
